<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
	<div
		v-if="isVisible"
		class="absolute top-xs left-xs flex flex-col overflow-hidden rounded"
		:style="variables"
		:class="{
			'max-w-[60%]': isSearch,
			'max-w-[65%] lg:max-w-[90%]': !isSearch,
			// background
			'bg-(--main-background)': isCustomMainBackgroundColor,
			'bg-white': mainTheme?.background?.color === 'WHITE',
			'bg-black': mainTheme?.background?.color === 'BLACK',
			'bg-yellow-500': mainTheme?.background?.color === 'YELLOW',
			'bg-red-500': !mainTheme || mainTheme.background?.color === 'RED',
			// text
			'text-(--main-text)': isCustomMainTextColor,
			'text-black': mainTheme?.text?.color === 'BLACK',
			'text-yellow-500': mainTheme?.text?.color === 'YELLOW',
			'text-red-500': mainTheme?.text?.color === 'RED',
			'text-white': !mainTheme || mainTheme.text?.color === 'WHITE',
		}"
	>
		<div
			v-if="canShowAmountRow"
			class="flex flex-col items-center justify-center px-1.5 text-center font-prenton-cond leading-none tracking-2xl uppercase"
			:class="{
				'md:py-2': isSearch,
				'md:py-1.5': !isSearch && !isUsedInSearchModal,
				'pb-0.5': isUsedInSearchModal,
				'order-1': isStyleMirrored,
			}"
		>
			<p
				v-if="themeType && nonAmountPromotions.has(themeType)"
				class="py-1 leading-none md:py-0 md:leading-none"
				:style="variables"
				:class="{
					'md:text-xs': isSearch,
					'font-prenton-cond': mainTheme?.font?.family === 'PRENTON_COND',
					'font-sans-narrow': mainTheme?.font?.family === 'SANS_NARROW',
					'font-sans-caption': mainTheme?.font?.family === 'SANS_CAPTION',
					// size
					'text-xs md:text-sm lg:text-xs':
						mainTheme?.font?.size === 'EXTRA_SMALL',
					'text-xs md:text-sm lg:text-sm': mainTheme?.font?.size === 'SMALL',
					'text-xs md:text-sm lg:text-base': mainTheme?.font?.size === 'MEDIUM',
					'text-xs md:text-sm lg:text-lg': mainTheme?.font?.size === 'LARGE',
					'text-xs md:text-sm lg:text-xl':
						mainTheme?.font?.size === 'EXTRA_LARGE',
					'text-(length:--main-font-size)': mainTheme?.font?.size === 'CUSTOM',
				}"
			>
				<!-- eslint-disable-next-line vue/no-v-html -->
				<span v-html="promotion.style.content?.split('|').join('<br>')"></span>
			</p>

			<p
				v-else-if="
					['PERCENT', 'PERCENT_WITH_AMOUNT', 'PERCENT_WITH_TEXT'].includes(
						themeType,
					) &&
					promotion.discount.type !== 'CatalogPromotionFixedAmount' &&
					discountAmount > 0
				"
				class="py-1 md:py-0"
				:class="{
					'md:text-2xl': isSearch,
					'text-xl leading-none md:text-xl': isUsedInSearchModal,
					'text-2xl leading-none md:text-4xl': !isSearch,
					'font-prenton-cond': mainTheme?.font?.family === 'PRENTON_COND',
					'font-sans-narrow': mainTheme?.font?.family === 'SANS_NARROW',
					'font-sans-caption': mainTheme?.font?.family === 'SANS_CAPTION',
					// size
					'text-xs':
						mainTheme?.font?.size === 'EXTRA_SMALL' || isUsedInSearchModal,
					'text-sm': mainTheme?.font?.size === 'SMALL',
					'text-base':
						mainTheme?.font?.size === 'MEDIUM' || !isUsedInSearchModal,
					'text-lg': mainTheme?.font?.size === 'LARGE',
					'text-xl': mainTheme?.font?.size === 'EXTRA_LARGE',
					'text-(length:--main-font-size)': mainTheme?.font?.size === 'CUSTOM',
				}"
			>
				<template
					v-if="promotion.discount.type === 'CatalogPromotionFixedAmount'"
				>
					-{{ formatCurrency(discountAmount) }}
				</template>

				<template v-else> -{{ discountAmount }}% </template>
			</p>

			<p
				v-else-if="
					canShowClubPrice && ['CLUB_PERCENT_WITH_TEXT'].includes(themeType)
				"
				class="p-0.5 text-lg"
				:class="{
					'md:text-2xl': isSearch,
					'text-2xl leading-none md:text-4xl':
						!isSearch && !isUsedInSearchModal,
					'font-prenton-cond': mainTheme?.font?.family === 'PRENTON_COND',
					'font-sans-narrow': mainTheme?.font?.family === 'SANS_NARROW',
					'font-sans-caption': mainTheme?.font?.family === 'SANS_CAPTION',
					// size
					'text-xs':
						mainTheme?.font?.size === 'EXTRA_SMALL' || isUsedInSearchModal,
					'text-sm': mainTheme?.font?.size === 'SMALL',
					'text-base': mainTheme?.font?.size === 'MEDIUM',
					'text-lg': mainTheme?.font?.size === 'LARGE',
					'text-xl': mainTheme?.font?.size === 'EXTRA_LARGE',
					'text-(length:--main-font-size)': mainTheme?.font?.size === 'CUSTOM',
				}"
			>
				<template
					v-if="promotion.discount.type === 'CatalogPromotionFixedAmount'"
				>
					-{{ formatCurrency(discountAmount) }}
				</template>

				<template v-else> -{{ clubDiscountAmount }}% </template>
			</p>
		</div>

		<p
			v-if="themeType === 'PERCENT_WITH_AMOUNT' && savedAmount > 0 && !isSearch"
			class="px-1 text-center font-sans text-xs leading-4 font-bold md:px-2 md:leading-[22px]"
			:class="{
				'hidden md:block md:h-6 md:px-2 md:text-[16px]': isSearch,
				// background
				'bg-(--main-background)': isCustomAltTextColor,
				'bg-white': altTheme?.background?.color === 'WHITE',
				'bg-black': altTheme?.background?.color === 'BLACK',
				'bg-yellow-500': altTheme?.background?.color === 'YELLOW',
				'bg-orange-500': altTheme?.background?.color === 'ORANGE',
				'bg-red-500': !altTheme || altTheme.background?.color === 'RED',
				// text
				'text-(--alt-text)': isCustomAltTextColor,
				'text-black': altTheme?.text?.color === 'BLACK',
				'text-yellow-500': altTheme?.text?.color === 'YELLOW',
				'text-orange-500': altTheme?.text?.color === 'ORANGE',
				'text-red-500': altTheme?.text?.color === 'RED',
				'text-white': !altTheme || altTheme.text?.color === 'WHITE',
				//font
				'font-prenton-cond': altTheme?.font?.family === 'PRENTON_COND',
				'font-sans-narrow': altTheme?.font?.family === 'SANS_NARROW',
				'font-sans-caption': altTheme?.font?.family === 'SANS_CAPTION',
				// size
				'text-xs md:text-sm lg:text-xs': altTheme?.font?.size === 'EXTRA_SMALL',
				'text-xs md:text-sm lg:text-sm': altTheme?.font?.size === 'SMALL',
				'text-xs md:text-sm lg:text-base': altTheme?.font?.size === 'MEDIUM',
				'text-xs md:text-sm lg:text-lg': altTheme?.font?.size === 'LARGE',
				'text-xs md:text-sm lg:text-xl': altTheme?.font?.size === 'EXTRA_LARGE',
				'text-(length:--main-font-size)': altTheme?.font?.size === 'CUSTOM',
				// mirrored
				'order-1': isStyleMirrored,
			}"
		>
			{{ $t("discount.save", {amount: formatCurrency(savedAmount)}) }}
		</p>

		<p
			v-else-if="themeType === 'PERCENT_WITH_TEXT'"
			class="hidden p-1 text-center font-sans font-bold md:block"
			:class="{
				'md:px-2': !isUsedInSearchModal,
				// background
				'bg-(--alt-background)': isCustomAltBackgroundColor,
				'bg-white': altTheme?.background?.color === 'WHITE',
				'bg-black': altTheme?.background?.color === 'BLACK',
				'bg-yellow-500': altTheme?.background?.color === 'YELLOW',
				'bg-orange-500': altTheme?.background?.color === 'ORANGE',
				'bg-red-500': !altTheme || altTheme.background?.color === 'RED',
				// text
				'text-(--alt-text)': isCustomAltTextColor,
				'text-black': altTheme?.text?.color === 'BLACK',
				'text-yellow-500': altTheme?.text?.color === 'YELLOW',
				'text-orange-500': altTheme?.text?.color === 'ORANGE',
				'text-red-500': altTheme?.text?.color === 'RED',
				'text-white': !altTheme || altTheme.text?.color === 'WHITE',
				//font
				'font-prenton-cond': altTheme?.font?.family === 'PRENTON_COND',
				'font-sans-narrow': altTheme?.font?.family === 'SANS_NARROW',
				'font-sans-caption': altTheme?.font?.family === 'SANS_CAPTION',
				// size
				'text-xs lg:text-xs': altTheme?.font?.size === 'EXTRA_SMALL',
				'text-xs lg:text-sm': altTheme?.font?.size === 'SMALL',
				'text-xs md:text-base':
					altTheme?.font?.size === 'MEDIUM' && !isUsedInSearchModal && isSearch,
				'text-xs md:text-xs':
					altTheme?.font?.size === 'MEDIUM' && isUsedInSearchModal && !isSearch,
				'text-xs lg:text-lg': altTheme?.font?.size === 'LARGE',
				'text-xs lg:text-xl': altTheme?.font?.size === 'EXTRA_LARGE',
				'text-(length:--alt-font-size)':
					altTheme?.font?.size === 'CUSTOM' && !isSearch,
				'px-1 text-xs': isUsedInSearchModal || !isSearch,
			}"
		>
			{{ promotion.style.altContent }}
		</p>

		<p
			v-if="
				clubDiscountAmount &&
				clubDiscountAmount > 0 &&
				themeType === 'CLUB_PERCENT_WITH_TEXT'
			"
			class="px-1 text-center font-sans font-bold md:px-2"
			:class="{
				// background
				'bg-(--alt-background)': isCustomAltBackgroundColor,
				'bg-white': altTheme?.background?.color === 'WHITE',
				'bg-black': altTheme?.background?.color === 'BLACK',
				'bg-yellow-500': altTheme?.background?.color === 'YELLOW',
				'bg-orange-500': altTheme?.background?.color === 'ORANGE',
				'bg-red-500': !altTheme || altTheme.background?.color === 'RED',
				// text
				'text-(--alt-text)': isCustomAltTextColor,
				'text-black': altTheme?.text?.color === 'BLACK',
				'text-yellow-500': altTheme?.text?.color === 'YELLOW',
				'text-orange-500': altTheme?.text?.color === 'ORANGE',
				'text-red-500': altTheme?.text?.color === 'RED',
				'text-white': !altTheme || altTheme.text?.color === 'WHITE',
				//font
				'font-prenton-cond': altTheme?.font?.family === 'PRENTON_COND',
				'font-sans-narrow': altTheme?.font?.family === 'SANS_NARROW',
				'font-sans-caption': altTheme?.font?.family === 'SANS_CAPTION',
				// size
				'text-xs md:text-sm lg:text-xs': altTheme?.font?.size === 'EXTRA_SMALL',
				'text-xs md:text-sm lg:text-sm': altTheme?.font?.size === 'SMALL',
				'text-xs md:text-sm lg:text-base': altTheme?.font?.size === 'MEDIUM',
				'text-xs md:text-sm lg:text-lg': altTheme?.font?.size === 'LARGE',
				'text-xs md:text-sm lg:text-xl': altTheme?.font?.size === 'EXTRA_LARGE',
				'text-(length:--alt-font-size)':
					altTheme?.font?.size === 'CUSTOM' && !isSearch,
				'p-1 text-sm': isUsedInSearchModal,
			}"
		>
			<template v-if="canShowClubPrice">
				{{ promotion.style.altContent }}
			</template>

			<template v-else>
				{{ t("club-price-cta") }}
			</template>
		</p>
	</div>
</template>

<script setup lang="ts">
import type {PromotionData} from "@pkg/shared/types";

import {useCurrency} from "@pkg/shared/composables/use-currency";
import {usePromotionCalculations} from "@pkg/shared/composables/use-promotion-calculations";

// NOTE: Also see the other sale-tag component we have!

const properties = defineProps<{
	promotion: PromotionData;
	price: number;
	originalPrice: number;
	clubPrice?: number;
	isSearch?: boolean; /** This is when the page is search/index */
	isUsedInSearchModal?: boolean; /** This is true when the search happens from the search bar */
	canShowClubPrice: boolean; /* True for logged in users */
}>();

const {format: formatCurrency} = useCurrency();

const {t} = useI18n();

const {totalDiscountAmount} = usePromotionCalculations({
	promotionType: properties.promotion.discount.type,
	price: Number(properties.price),
	originalPrice: Number(properties.originalPrice),
	clubPrice: properties.clubPrice,
	canShowClubPrice: properties.canShowClubPrice,
});
const discountAmount = computed(() =>
	totalDiscountAmount.value === undefined ? 0 : totalDiscountAmount.value,
);

const clubDiscountAmount = computed<number | undefined>(() => {
	if (
		properties.promotion.discount.type === "CatalogPromotionPercent" &&
		properties.clubPrice
	) {
		return Math.round(
			(1 - properties.clubPrice / properties.originalPrice) * 100,
		);
	}

	return undefined;
});

const savedAmount = computed(() => properties.originalPrice - properties.price);

const themeType = computed(() => properties.promotion.style.type);

const canShowAmountRow = computed(() => {
	if (!["CLUB_PERCENT_WITH_TEXT"].includes(themeType.value)) {
		return true;
	}

	return properties.canShowClubPrice;
});

const nonAmountPromotions = new Set(["TEXT", "OMNIBUS"]);

const isVisible = computed(() => {
	const {style} = properties.promotion;
	if (!style) {
		return false;
	}
	if (nonAmountPromotions.has(style.type) && style.content) {
		return true;
	}
	if (
		!nonAmountPromotions.has(style.type) &&
		discountAmount.value !== undefined &&
		discountAmount.value > 0
	) {
		// If we have a promotion that's amount-based, we might still hit the edge case where we should render a club price instead (check the `canShowClubPrice` section in the template). In that case, we want to rule out showing the tag in the edge case that the club discount is actually non-existent.
		// eslint-disable-next-line unicorn/prefer-boolean-return
		if (
			properties.canShowClubPrice &&
			["CLUB_PERCENT_WITH_TEXT"].includes(themeType.value) &&
			properties.promotion.discount.type !== "CatalogPromotionFixedAmount" &&
			(clubDiscountAmount.value ?? 0) <= 0
		) {
			return false;
		}

		return true;
	}
	if (
		style.type === "CLUB_PERCENT_WITH_TEXT" &&
		properties.clubPrice !== undefined &&
		Number(properties.clubPrice) > 0
	) {
		return (
			(properties.promotion.discount.type === "CatalogPromotionFixedAmount" &&
				(discountAmount.value ?? 0) > 0) ||
			(clubDiscountAmount.value ?? 0 > 0)
		);
	}
	return false;
});

const mainTheme = computed(() => properties.promotion.style.mainTheme);
const altTheme = computed(() => properties.promotion.style.altTheme);

const isStyleMirrored = computed(() => properties.promotion.style?.mirrored);

// Custom Main
const isCustomMainBackgroundColor = computed(
	() => mainTheme.value?.background?.color === "CUSTOM",
);
const isCustomMainTextColor = computed(
	() => mainTheme.value?.text?.color === "CUSTOM",
);
const mainFontSize = computed(() => mainTheme.value?.font?.size);
const mainFontFamily = computed(() => mainTheme.value?.font?.family);
const isCustomMainFontSize = computed(
	() => mainTheme.value?.font?.size === "CUSTOM",
);

// Custom Alt
const isCustomAltBackgroundColor = computed(
	() => altTheme.value?.background?.color === "CUSTOM",
);
const isCustomAltTextColor = computed(
	() => altTheme.value?.text?.color === "CUSTOM",
);
const isCustomAltFontSize = computed(
	() => altTheme.value?.font?.size === "CUSTOM",
);

const fontFamily = ref("");

const variables = computed(() => ({
	...(isCustomMainBackgroundColor && {
		"--main-background": mainTheme.value?.background?.customColor ?? undefined,
	}),
	...(isCustomMainTextColor && {
		"--main-text": mainTheme.value?.text?.customColor ?? undefined,
	}),
	...(isCustomAltBackgroundColor && {
		"--alt-background": altTheme.value?.background?.customColor ?? undefined,
	}),
	...(isCustomAltTextColor && {
		"--alt-text": altTheme.value?.text?.customColor ?? undefined,
	}),
	...(mainFontFamily && {
		"--font-family": fontFamily.value ?? undefined,
	}),
	...(mainFontSize && {
		"--font-size": mainTheme.value?.font?.size || undefined,
	}),
	...(isCustomMainFontSize && {
		"--main-font-size": mainTheme.value?.font?.customSize
			? `${mainTheme.value?.font?.customSize}px`
			: undefined,
	}),
	...(isCustomAltFontSize && {
		"--alt-font-size": altTheme.value?.font?.customSize
			? `${altTheme.value?.font?.customSize}px`
			: undefined,
	}),
}));
</script>

<i18n lang="json">
{
	"et": {
		"club-price-cta": "Vaata klubihinda"
	},
	"fi": {
		"club-price-cta": "Katso klubihinta"
	}
}
</i18n>
